import { server } from '@/helpers';
import { z } from 'zod';

const saveExternalRequest = z.object({
  placeId: z.number(),
  stars: z.number(),
  message: z.string().optional(),
});

type SaveExternalRequest = z.infer<typeof saveExternalRequest>;

const updateExternalRequest = z.object({
  reviewId: z.number(),
  placeId: z.number(),
  stars: z.number().optional(),
  message: z.string().optional(),
});

type UpdateExternalRequest = z.infer<typeof updateExternalRequest>;

export const getPlaceRatingSummaryResponseSchema = z.object({
  averageScore: z.number(),
  ratingCount: z.number(),
});

type GetPlaceRatingSummaryResponse = z.infer<typeof getPlaceRatingSummaryResponseSchema>;

const reviewSchema = z.object({
  id: z.number(),
  canEdit: z.boolean(),
  verified: z.boolean(),
  review: z.object({
    score: z.number(),
    createdAt: z.string(),
    author: z.string().optional(),
    text: z.string().optional(),
  }),
  companyReply: z
    .object({
      text: z.string().optional(),
      createdAt: z.string().optional(),
    })
    .optional(),
});

export type Review = z.infer<typeof reviewSchema>;

export const getPlaceReviewsResponseSchema = z.object({
  reviews: z.array(reviewSchema),
  totalPages: z.number(),
  totalReviews: z.number(),
});

type GetPlaceReviewsResponse = z.infer<typeof getPlaceReviewsResponseSchema>;

const getPlaceReviews = (placeId: number, page: number, limit: number): GetPlaceReviewsResponse =>
  server.request
    .get(`/places/reviews/${placeId}`, {
      params: {
        page,
        limit,
      },
    })
    .then(server.handleSuccess)
    .catch(server.handleErrorObject);

const getPlaceRatingSummary = (placeId: number): GetPlaceRatingSummaryResponse =>
  server.request
    .get('/places/reviews/summary/' + placeId)
    .then(server.handleSuccess)
    .catch(server.handleErrorObject);

const getReviewData = (userId, bookingId, reviewId) =>
  server.request
    .get(`/reviews/booking-review/${userId}/${bookingId}/` + (reviewId ? '?reviewId=' + reviewId : ''))
    .then(server.handleSuccess)
    .catch(server.handleError);

const save = (data) => server.request.post(`/reviews/save`, data).then(server.handleSuccess).catch(server.handleError);

const saveExternal = (data: SaveExternalRequest) =>
  server.request.post(`/reviews/external/save`, data).then(server.handleSuccess).catch(server.handleErrorObject);

const updateExternal = (data: UpdateExternalRequest) =>
  server.request.post(`/reviews/external/update`, data).then(server.handleSuccess).catch(server.handleErrorObject);

const deleteExternal = (reviewId: string) =>
  server.request.delete(`/reviews/external/${reviewId}`).then(server.handleSuccess).catch(server.handleErrorObject);

const uploadFile = (data) =>
  server.request
    .post(`/reviews/upload-file`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    .then(server.handleSuccess)
    .catch(server.handleError);

const deleteFile = (data) =>
  server.request.post(`/support-form/delete-file`, data).then(server.handleSuccess).catch(server.handleError);

export const reviewsServices = {
  save,
  uploadFile,
  deleteFile,
  getReviewData,
  saveExternal,
  updateExternal,
  deleteExternal,
  getPlaceReviews,
  getPlaceRatingSummary,
};
